import { useMemo, useState } from "react"
import { toast } from "sonner"
import { CalendarDate } from "@untitledui/icons"

import { SlotPicker } from "@/components/calendar/slot-picker"
import { PaneSkeleton } from "./skeletons"
import { useCreateAppointment, useSlots } from "@/lib/api/use-appointments"

// A booking is a booking. The customer says "Thursday works", she picks
// the slot right here, and POST /appointments does the rest: the
// appointment exists, the estimate is Scheduled, the open tasks close.

interface BookInlineProps {
  teamId: number
  estimateId: number
  customerName: string
  onBooked: (appointmentId: number, startsAt: string) => void
  /** Runs before the booking is created — a no-show re-book records the
   *  no-show FIRST, because that write rolls the estimate back to In Progress
   *  and would undo a booking made a moment earlier. */
  beforeBook?: () => Promise<void>
  /** Four slots per row (a wide branch) or three (the side pane). */
  compact?: boolean
}

export function BookInline({ teamId, estimateId, customerName, onBooked, beforeBook, compact = true }: BookInlineProps) {
  const from = useMemo(() => new Date().toISOString(), [])
  const to = useMemo(() => new Date(Date.now() + 14 * 86_400_000).toISOString(), [])
  const slotsQ = useSlots(teamId, from, to)
  const create = useCreateAppointment()
  const [picked, setPicked] = useState<string | null>(null)
  const [filled, setFilled] = useState(false)
  const [preparing, setPreparing] = useState(false)

  const book = async () => {
    if (!picked || create.isPending || preparing) return
    setFilled(false)
    if (beforeBook) {
      setPreparing(true)
      try {
        await beforeBook()
      } catch {
        setPreparing(false)
        toast.error("Couldn't record the no-show — nothing was booked.")
        return
      }
      setPreparing(false)
    }
    create.mutate(
      // The calendar still stores a kind; the shop never chooses one here.
      { teamId, estimateId, kind: "dropoff", startsAt: picked },
      {
        onSuccess: (appt) => onBooked(appt.id, picked),
        onError: (err) => {
          // The shop route refuses a slot that just filled (409). Refetch
          // and let her pick again rather than a dead-end toast.
          const status = (err as { status?: number })?.status
          if (status === 409) {
            setFilled(true)
            setPicked(null)
            void slotsQ.refetch()
          } else {
            toast.error("Couldn't book that slot.")
          }
        },
      },
    )
  }

  return (
    <div>
      {slotsQ.isLoading ? (
        <PaneSkeleton />
      ) : (
        <SlotPicker slots={slotsQ.data ?? []} value={picked} onChange={setPicked} compact={compact} days={compact ? 8 : 5} />
      )}

      {filled && <p className="mt-2 text-[13px] font-medium text-red-600">That time just filled up — pick another.</p>}

      {/* Select-then-confirm: one stray tap must never create a real booking. */}
      <button
        type="button"
        disabled={!picked || create.isPending}
        onClick={book}
        className="mt-3 w-full h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[14px] font-semibold transition-[background-color,transform] duration-150 ease-out-quart active:scale-[0.99] motion-reduce:transition-none disabled:opacity-40 disabled:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40 focus-visible:ring-offset-1 inline-flex items-center justify-center gap-2"
      >
        <CalendarDate className="size-4" aria-hidden />
        {create.isPending
          ? "Booking…"
          : picked
            ? `Book ${customerName.split(" ")[0]} — ${new Date(picked).toLocaleString("en-US", {
                weekday: "short",
                hour: "numeric",
                minute: "2-digit",
              })}`
            : "Pick a time"}
      </button>
    </div>
  )
}
